/**
 * Detail/edit view for a journal entry.
 */
import { createRoute, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, Show } from "solid-js";
import { ArrowLeft, History, Star, Trash2 } from "@fleet/icons";
import { cn } from "@journal/shared";
import { useEntryByIdQuery, useEntryTagsQuery, usePhotosByEntryQuery } from "@journal/shared/solid";
import { appRoute } from "./route";
import { PageContainer } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { EntryComposer } from "@/features/journal/components/entry-composer";
import { VersionHistorySheet } from "@/features/journal/components/version-history-sheet";
import { PhotoGrid } from "@/features/journal/components/photo-grid";
import { TagPicker } from "@/features/journal/components/tag-picker";
import { formatDayHeading } from "@/features/journal/format";
import { useJournalData } from "@/features/journal/hooks/use-journal-data";
import { useEntryCarousel } from "@/features/journal/hooks/use-entry-carousel";
import { useEntryMutations } from "@/features/journal/hooks/use-entry-mutations";

function EntryDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const params = entryDetailRoute.useParams();
  const search = useSearch({ from: "/_app/entry/$entryId" });
  const { db, drizzle, userId, staleTime } = useJournalData();

  // Shareable photo lightbox: the enlarged photo lives in `?photo=<id>`, so
  // copying the URL reopens exactly that photo in another browser. It is the
  // source of truth; we write with `replace` to avoid bloating history.
  const openPhotoId = () => search().photo ?? null;
  const setOpenPhotoId = (photoId: string | null) => {
    if ((search().photo ?? null) === photoId) {
      return;
    }
    void navigate({
      to: "/entry/$entryId",
      params: { entryId: params().entryId },
      search: { photo: photoId ?? undefined },
      replace: true,
    });
  };
  const {
    setFavorite,
    trashEntry,
    restoreVersion,
    addTagToEntry,
    removeTagFromEntry,
    deletePhoto,
  } = useEntryMutations();

  const [confirmOpen, setConfirmOpen] = createSignal(false);
  const [historyOpen, setHistoryOpen] = createSignal(false);
  const [restoring, setRestoring] = createSignal(false);
  const [surfaceRef, setSurfaceRef] = createSignal<HTMLDivElement>();

  const entry = useEntryByIdQuery({ db, drizzle, staleTime, entryId: () => params().entryId });

  // Resolves the entry: present / loading (sync) / not-found / offline.
  // A deleted entry, an invalid ID, or no access (sync rules do not download
  // other rows) must not leave the screen loading or show an empty shell.
  const resolution = createEntityResolution({
    db,
    hasEntity: () => Boolean(entry.data),
    isPending: () => entry.isLoading,
  });

  const photos = usePhotosByEntryQuery({
    db,
    drizzle,
    staleTime,
    entryId: () => params().entryId,
  });
  const entryTags = useEntryTagsQuery({
    db,
    drizzle,
    staleTime,
    entryId: () => params().entryId,
  });

  const selectedTagIds = createMemo(() => (entryTags.data ?? []).map((tag) => tag.id));

  const handleTagsChange = (next: string[]) => {
    const current = new Set(selectedTagIds());
    const target = new Set(next);
    for (const id of next) {
      if (!current.has(id)) {
        void addTagToEntry(params().entryId, id);
      }
    }
    for (const id of current) {
      if (!target.has(id)) {
        void removeTagFromEntry(params().entryId, id);
      }
    }
  };

  const isFavorite = () => entry.data?.is_favorite === 1;

  // Horizontal swipe pages between the sibling entries of this day.
  const carousel = useEntryCarousel({
    db,
    drizzle,
    userId,
    staleTime,
    entryId: () => params().entryId,
    entryDate: () => entry.data?.entry_date ?? "",
    navigate: (id) => void navigate({ to: "/entry/$entryId", params: { entryId: id } }),
    getSurfaceRef: () => surfaceRef(),
  });

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <PageContainer maxWidth="2xl" class="flex min-h-0 flex-1 items-center justify-center pb-16">
          <NotFoundState
            variant={resolution() === "offline" ? "offline" : "not-found"}
            title={resolution() === "offline" ? undefined : t(msg`Entry not found`)}
            description={
              resolution() === "offline"
                ? undefined
                : t(msg`This entry may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to journal`),
              onClick: () => {
                void navigate({ to: "/" });
              },
            }}
          />
        </PageContainer>
      }
    >
      <PageContainer maxWidth="2xl" class="overflow-x-clip pb-16">
        {/* Swipe surface owns the horizontal gesture; `data-swipe-own` makes the
          layout-level page swipe defer to it, and `touch-action: pan-y` keeps
          native vertical scroll while we claim the horizontal axis. */}
        <div data-swipe-own style={{ "touch-action": "pan-y" }} {...carousel.handlers}>
          <div
            ref={setSurfaceRef}
            class="flex flex-col gap-5 will-change-transform"
            style={{ transform: carousel.transform(), transition: carousel.transition() }}
            data-testid="entry-swipe-surface"
          >
            <header class="flex items-center gap-2 pt-2">
              <button
                type="button"
                class="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={t(msg`Back`)}
                onClick={() => {
                  globalThis.history.back();
                }}
              >
                <ArrowLeft class="h-4 w-4" />
              </button>
              <h1 class="flex-1 truncate text-lg font-bold capitalize text-foreground">
                {formatDayHeading(entry.data?.entry_date ?? "", locale())}
              </h1>
              <button
                type="button"
                data-testid="detail-history"
                class="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                aria-label={t(msg`Version history`)}
                onClick={() => setHistoryOpen(true)}
              >
                <History class="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="detail-favorite"
                class={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground",
                  isFavorite() && "text-amber-400 hover:text-amber-400",
                )}
                aria-label={isFavorite() ? t(msg`Remove from favorites`) : t(msg`Add to favorites`)}
                aria-pressed={isFavorite()}
                onClick={() => {
                  void setFavorite(params().entryId, !isFavorite());
                }}
              >
                <Star class={cn("h-4 w-4", isFavorite() && "fill-current")} />
              </button>
              <Button
                variant="ghost"
                size="sm"
                class="gap-1.5 text-muted-foreground hover:text-destructive"
                data-testid="detail-delete"
                onClick={() => setConfirmOpen(true)}
              >
                <Trash2 class="h-4 w-4" />
                {t(msg`Delete`)}
              </Button>
            </header>

            {/* `resolution` already covers absence (not-found/offline); only
              la ventana breve de carga inicial mientras `entry.data` se asienta. */}
            <Show when={entry.data} fallback={null}>
              {(found) => (
                <>
                  <EntryComposer dateKey={() => found().entry_date ?? ""} entry={() => found()} />

                  <section class="flex flex-col gap-2">
                    <h2 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {t(msg`Tags`)}
                    </h2>
                    <TagPicker selectedIds={selectedTagIds} onChange={handleTagsChange} />
                  </section>

                  <Show when={(photos.data ?? []).length > 0}>
                    <section class="flex flex-col gap-2">
                      <h2 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(msg`Photos`)}
                      </h2>
                      <PhotoGrid
                        photos={() => photos.data ?? []}
                        openPhotoId={openPhotoId}
                        onOpenPhotoChange={setOpenPhotoId}
                        onDelete={(photoId) => {
                          void deletePhoto(photoId);
                        }}
                      />
                    </section>
                  </Show>
                </>
              )}
            </Show>
          </div>
        </div>

        <ConfirmDialog
          title={t(msg`Delete this entry?`)}
          description={t(msg`The entry will be moved to the trash. You can restore it from there.`)}
          variant="destructive"
          open={confirmOpen()}
          onOpenChange={setConfirmOpen}
          onConfirm={() => {
            void (async () => {
              const ok = await trashEntry(params().entryId);
              if (ok) {
                void navigate({ to: "/" });
              }
            })();
          }}
        />

        <VersionHistorySheet
          open={historyOpen()}
          onOpenChange={setHistoryOpen}
          entryId={() => params().entryId}
          entry={() => entry.data ?? null}
          restoring={restoring}
          onRestore={(version) => {
            void (async () => {
              setRestoring(true);
              try {
                const ok = await restoreVersion(params().entryId, version);
                if (ok) {
                  setHistoryOpen(false);
                }
              } finally {
                setRestoring(false);
              }
            })();
          }}
        />
      </PageContainer>
    </Show>
  );
}

interface EntryDetailSearch {
  /** Enlarged photo in the lightbox (G4 #15b): its own copyable per-photo URL. */
  photo?: string;
}

export const entryDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/entry/$entryId",
  validateSearch: (raw: Record<string, unknown>): EntryDetailSearch => ({
    photo: typeof raw.photo === "string" && raw.photo !== "" ? raw.photo : undefined,
  }),
  component: EntryDetailPage,
});
